import { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.bearing-intelligence.com';
const DEVICE_UUID_KEY = 'bi_device_uuid';

// Get or create a stable device UUID — never linked to identity
export function getDeviceUUID() {
  let uuid = localStorage.getItem(DEVICE_UUID_KEY);
  if (!uuid) {
    uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = Math.random() * 16 | 0;
      return (c === 'x' ? r : ((r & 0x3) | 0x8)).toString(16);
    });
    localStorage.setItem(DEVICE_UUID_KEY, uuid);
  }
  return uuid;
}

const SIGNAL_LABELS = {
  1: { label: 'Low', color: '#555', desc: 'Minimal relevance' },
  2: { label: 'Noteworthy', color: '#8a9bbf', desc: 'Worth monitoring' },
  3: { label: 'Significant', color: '#c4a563', desc: 'Operationally relevant' },
  4: { label: 'High', color: '#ff9933', desc: 'High priority signal' },
  5: { label: 'Critical', color: '#ff3333', desc: 'Critical signal' },
};

export function SignalRating({ briefingId, sectionKey, initialRating, initialAggregate }) {
  const [myRating,   setMyRating]   = useState(initialRating || 0);
  const [aggregate,  setAggregate]  = useState(initialAggregate || null);
  const [hovering,   setHovering]   = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [expanded,   setExpanded]   = useState(false);

  const deviceUUID = getDeviceUUID();
  const display = hovering || myRating;
  const displayInfo = SIGNAL_LABELS[display];

  const submitRating = useCallback(async (rating) => {
    if (submitting) return;
    setSubmitting(true);
    setMyRating(rating);
    try {
      const res = await fetch(`${API_URL}/api/ratings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          briefing_id: briefingId,
          section_key: sectionKey,
          rating,
          device_uuid: deviceUUID,
        }),
      });
      const data = await res.json();
      if (data.aggregate) setAggregate(data.aggregate);
    } catch(e) {}
    finally { setSubmitting(false); }
  }, [briefingId, sectionKey, deviceUUID, submitting]);

  return (
    <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {/* Label */}
        <span style={{ color: '#444', fontSize: 10, fontWeight: 'bold', letterSpacing: '0.5px', flexShrink: 0 }}>
          SIGNAL
        </span>

        {/* 5 stars */}
        <div style={{ display: 'flex', gap: 3 }}>
          {[1,2,3,4,5].map(n => (
            <button key={n}
              onClick={() => submitRating(n)}
              onMouseEnter={() => setHovering(n)}
              onMouseLeave={() => setHovering(0)}
              style={{
                width: 20, height: 20, borderRadius: 3, border: 'none',
                cursor: 'pointer', fontSize: 11, fontWeight: 'bold',
                transition: 'all 0.1s',
                backgroundColor: n <= display
                  ? (SIGNAL_LABELS[display]?.color || '#c4a563') + '33'
                  : 'rgba(255,255,255,0.05)',
                color: n <= display
                  ? (SIGNAL_LABELS[display]?.color || '#c4a563')
                  : '#333',
              }}>
              {n}
            </button>
          ))}
        </div>

        {/* Current selection label */}
        {display > 0 && (
          <span style={{ color: displayInfo?.color, fontSize: 11, fontWeight: 'bold' }}>
            {displayInfo?.label}
          </span>
        )}
        {display === 0 && (
          <span style={{ color: '#333', fontSize: 11, fontStyle: 'italic' }}>Rate signal strength</span>
        )}

        {/* Aggregate toggle */}
        {aggregate && parseInt(aggregate.total_ratings) > 0 && (
          <button onClick={() => setExpanded(e => !e)} style={{
            marginLeft: 'auto', background: 'none', border: 'none',
            color: '#444', cursor: 'pointer', fontSize: 10,
          }}>
            {expanded ? '▲' : `${aggregate.total_ratings} rating${aggregate.total_ratings > 1 ? 's' : ''} ▼`}
          </button>
        )}
      </div>

      {/* Aggregate breakdown */}
      {expanded && aggregate && (
        <div style={{ marginTop: 7, padding: '7px 10px', backgroundColor: 'rgba(255,255,255,0.02)',
          borderRadius: 5, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 11, color: '#666' }}>
            Avg signal: <span style={{ color: '#c4a563', fontWeight: 'bold' }}>{aggregate.avg_rating}/5</span>
          </div>
          <div style={{ fontSize: 11, color: '#666' }}>
            High-priority flags: <span style={{ color: '#ff9933', fontWeight: 'bold' }}>{aggregate.high_signal_count}</span>
          </div>
          <div style={{ fontSize: 11, color: '#666' }}>
            Total ratings: <span style={{ color: '#888' }}>{aggregate.total_ratings}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// Hook to load all ratings for a briefing
export function useBriefingRatings(briefingId) {
  const [ratings,   setRatings]   = useState({});
  const [myRatings, setMyRatings] = useState({});

  useEffect(() => {
    if (!briefingId) return;
    const deviceUUID = getDeviceUUID();
    fetch(`${API_URL}/api/ratings/${briefingId}?device_uuid=${deviceUUID}`)
      .then(r => r.json())
      .then(data => {
        const agg = {};
        (data.aggregates || []).forEach(a => { agg[a.section_key] = a; });
        setRatings(agg);
        setMyRatings(data.myRatings || {});
      })
      .catch(() => {});
  }, [briefingId]);

  return { ratings, myRatings };
}
